import { toast } from "react-toastify";

/**
 * Parses and triggers NOC document download/preview from backend response.
 * Handles all nested data structures:
 * - resData.data.data (Array of items containing presigned url or FileData)
 * - resData.data (Array / Object)
 * - Presigned AWS S3 URLs or Base64 FileData stream
 */
export const processNocDownloadResponse = (
  resData: any,
  defaultErrorMessage = "Failed to download NOC document."
): boolean => {
  if (!resData) {
    toast.error(defaultErrorMessage);
    return false;
  }

  // 1. Unpack nested data containers
  const innerData = resData?.data;
  let items: any[] = [];

  if (Array.isArray(innerData?.data)) {
    items = innerData.data;
  } else if (Array.isArray(innerData)) {
    items = innerData;
  } else if (Array.isArray(resData?.data)) {
    items = resData.data;
  } else if (Array.isArray(resData)) {
    items = resData;
  }

  // 2. Extract first document item or fallback object
  const docItem =
    items[0] ||
    (typeof innerData === "object" && !Array.isArray(innerData) ? innerData : null) ||
    resData;

  const url = docItem?.url || docItem?.fileUrl || docItem?.downloadUrl || resData?.url;
  const fileData = docItem?.FileData || docItem?.content || docItem?.Content;
  const fileName = docItem?.FileName || docItem?.fileName || "NOC_Document.pdf";
  const fileType = docItem?.FileType || docItem?.fileExtension || "application/pdf";

  // 3. Open URL in new tab if presigned S3 url is available
  if (url && typeof url === "string" && (url.startsWith("http://") || url.startsWith("https://"))) {
    window.open(url, "_blank");
    return true;
  }

  // 4. Handle Base64 FileData stream if presigned url is absent
  if (fileData && typeof fileData === "string") {
    const mimeType = fileType.toLowerCase().includes("pdf") ? "application/pdf" : fileType;
    const dataUrl = fileData.startsWith("data:") ? fileData : `data:${mimeType};base64,${fileData}`;

    const newWindow = window.open();
    if (newWindow) {
      newWindow.document.write(
        `<iframe src="${dataUrl}" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>`
      );
      return true;
    } else {
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = fileName.endsWith(".pdf") ? fileName : `${fileName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return true;
    }
  }

  // 5. Fallback error message if no valid url or fileData found
  let msg = defaultErrorMessage;

  if (innerData?.success === false && innerData?.message) {
    msg = innerData.message;
  } else if (resData?.success === false && resData?.message) {
    msg = resData.message;
  } else if (innerData?.message && typeof innerData.message === 'string' && !innerData.message.toLowerCase().includes('success')) {
    msg = innerData.message;
  } else if (resData?.message && typeof resData.message === 'string' && !resData.message.toLowerCase().includes('success')) {
    msg = resData.message;
  }

  toast.error(msg);
  return false;
};
